import type { ReactNode } from "react";
import { AccessDenied } from "@/components/AccessDenied";
import { usePermissions } from "@/hooks/useUnifiedAuth";
import type { Permission } from "@/constants/permissions";
import type { PermissionRole } from "@/components/layout/navigation";

interface PermissionRouteProps {
  children: ReactNode;
  /** Basta UMA das permissões para liberar a rota. */
  anyOf: Permission[];
  /** Nome da área para a mensagem de acesso negado. */
  area?: string;
  /**
   * Papéis que liberam a rota enquanto o usuário NÃO foi migrado. Vem de
   * `getLegacyRolesForRoute`, para ser o mesmo critério do menu.
   *
   * Omitir significa "no regime legado esta rota não restringia ninguém" — que
   * é o caso da maioria, e o que preserva o acesso de hoje.
   */
  legacyRoles?: PermissionRole[];
}

/**
 * Trava de rota por permissão do catálogo GR-637.
 *
 * Vai por DENTRO do `Layout` (envolve o elemento da rota, não o bloco de
 * rotas): menu e header seguem montados, e a negativa aparece no lugar do
 * conteúdo. Rota negada sem menu é beco sem saída.
 *
 * PONTO ÚNICO das duas exceções do épico GR-561 — não repetir em cada rota:
 *
 *   - regra 10: interno (realm `musa`) passa em tudo. Ele nem tem as client
 *     roles de tela: o realm dele declara o mesmo `client_id` com um conjunto
 *     de backoffice.
 *   - regra 16: usuário NÃO MIGRADO passa reto. O token dele não tem client
 *     role nenhuma, então bloquear aqui derrubaria todo mundo que o script de
 *     migração ainda não alcançou. A trava só passa a valer de fato quando a
 *     pessoa entra nos grupos.
 *
 * Esconder o item no menu e negar a rota são coisas diferentes e as duas
 * precisam existir: o menu some, mas URL, bookmark e deep link continuam
 * chegando aqui.
 */
export const PermissionRoute = ({
  children,
  anyOf,
  area,
  legacyRoles,
}: PermissionRouteProps) => {
  const { hasAnyPermission, isMigrated, isInternal, isLoading, role } =
    usePermissions();
  // `permission_role` trafega como string; o filtro legado espera o união.
  const papel = role as PermissionRole | null;

  // Enquanto o perfil não resolve não dá para decidir: `isInternal` vem da
  // query e as permissões vêm do token. Liberar agora mostraria um flash da
  // tela negada; negar agora mostraria acesso negado a quem tem acesso.
  if (isLoading) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center">
        <div
          role="status"
          aria-label="Verificando acesso"
          className="h-8 w-8 animate-spin rounded-full border-b-2 border-primary-500"
        />
      </div>
    );
  }

  if (isInternal) return <>{children}</>;

  // Regime legado: o mesmo filtro por papel que o menu aplica.
  //
  // Liberar tudo aqui era incoerente — o menu escondia Financeiro do usuário
  // padrão e a URL entregava a tela inteira de faturas. A regra 16 manda não
  // BLOQUEAR quem a migração não alcançou, não conceder a ele mais do que
  // tinha; sem item de menu correspondente, a rota segue aberta como antes.
  if (!isMigrated) {
    if (!legacyRoles) return <>{children}</>;
    return papel && legacyRoles.includes(papel) ? (
      <>{children}</>
    ) : (
      <AccessDenied area={area} />
    );
  }

  if (!hasAnyPermission(...anyOf)) return <AccessDenied area={area} />;

  return <>{children}</>;
};

export default PermissionRoute;
